import {Box, Text} from '@chakra-ui/react';
import {OperationCommercialeCoverItem} from '@/domains/operation_commerciale/components/OperationCommercialeCoverItem.tsx';
import {useNavigate} from 'react-router-dom';
import {useApi} from '@/haligator-factory.tsx';
import {useHalState, useHalStates} from '@synako/haligator';
import {routes, usePanier} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {Flex, HStack} from '@chakra-ui/layout';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {OperationCommercialeSection} from '@/domains/operation_commerciale/controllers/OperationCommercialeSection.tsx';
import {useClientRoute} from '@/hooks/auth/useClientRoute';

interface OperationCommercialeViewProps {
  id_operation_commerciale: string;
}

/**
 * Affiche le détail d'une opération commerciale : bannière de présentation
 * puis liste des articles regroupés par catégorie.
 */
export const OperationCommercialeView = ({
  id_operation_commerciale,
}: OperationCommercialeViewProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();

  const lignesDePanier =
    useHalStates(usePanier().followAll('lignes_de_panier')).states ?? [];

  const operation_commercial_nav = useApi().follow(
    'get-operation-commerciale',
    {operation_commerciale_id: id_operation_commerciale},
  );
  const operation_commerciale_state = useHalState(operation_commercial_nav);
  const operation_commerciale = operation_commerciale_state.data!;

  const ligne_operation_commerciale_nav = operation_commercial_nav.follow(
    'lignes-operation_commerciale',
  );
  const ligne_operation_commerciale_state = useHalState(
    ligne_operation_commerciale_nav,
  );
  const ligne_operation_commerciale = ligne_operation_commerciale_state.data;
  const tri_selection = ligne_operation_commerciale?.tri_selection ?? [];

  if (operation_commerciale_state.loading) {
    return <></>;
  }

  const href = buildClientRoute(
    routes.client.mes_produits_operation_commerciale,
  );

  return (
    <Box
      bg={{lg: 'white'}}
      p={{base: '0', md: '4'}}
      mx='auto'
      mt='20px'
    >
      <Flex
        direction='column'
        gap={5}
      >
        <HStack
          _hover={{cursor: 'pointer'}}
          onClick={() => {
            navigate(href);
          }}
        >
          <ChevronLeftIcon color='gray.500' />
          <Text
            color='gray.500'
            fontSize='sm'
          >
            {t('operation_commerciale.retour')}
          </Text>
        </HStack>

        {/* Bandeau plus étroit que la liste d'articles et centré au-dessus. */}
        <Box
          w='100%'
          maxW='808px'
          mx='auto'
        >
          <OperationCommercialeCoverItem
            isLink={false}
            context='liste_detail'
            id_operation_commerciale={operation_commerciale.id}
            nom={operation_commerciale.nom!}
            description={operation_commerciale.description!}
            banniere_url={operation_commerciale.banniere_url!}
            banniere_url_mobile={
              operation_commerciale.banniere_url_mobile ?? undefined
            }
            banniere_couleur={
              operation_commerciale.banniere_couleur ?? undefined
            }
            banniere_cadrage={
              operation_commerciale.banniere_cadrage ?? undefined
            }
            date_fin={operation_commerciale.date_fin ?? undefined}
            affiche_date_fin={operation_commerciale.affiche_date_fin}
          />
        </Box>

        <Box
          border='1px solid'
          borderColor='gray.light'
          borderRadius='12px'
          boxShadow='md'
          overflow='hidden'
          mt='10px'
        >
          {tri_selection.map(({categorie_id, libelle_web, articles}) => {
            return (
              <OperationCommercialeSection
                id={categorie_id}
                key={categorie_id}
                title={libelle_web}
                // articles triés selon le critère choisi
                articles={articles}
                lignesDePanier={lignesDePanier}
              />
            );
          })}
        </Box>
      </Flex>
    </Box>
  );
};
